import { useMemo } from 'react';
import { Typography, Row, Col } from 'antd';
import { create } from 'jsondiffpatch';
import { format as formatHtmlDiff } from 'jsondiffpatch/formatters/html';
import 'jsondiffpatch/formatters/styles/html.css';
import './SnapshotDiffPanel.less';

const { Title, Text } = Typography;

const isIsoDateString = value => {
    if (typeof value !== 'string') return false;
    return /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
};

const toLocalDateTimeString = isoString => {
    try {
        const date = new Date(isoString);
        if (Number.isNaN(date.getTime())) return isoString;
        const pad = n => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    } catch (_) {
        return isoString;
    }
};

const normalizeDatesToLocal = data => {
    if (data === null || data === undefined) return data;
    if (Array.isArray(data)) {
        return data.map(item => normalizeDatesToLocal(item));
    }
    if (typeof data === 'object') {
        const result = {};
        Object.keys(data).forEach(key => {
            result[key] = normalizeDatesToLocal(data[key]);
        });
        return result;
    }
    if (isIsoDateString(data)) {
        return toLocalDateTimeString(data);
    }
    return data;
};

const parseJsonValue = value => {
    if (value === null || value === undefined) return value;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

const parseDataSnapshot = snapshot => {
    if (!snapshot) return { before: null, after: null };

    try {
        let parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
        while (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
        }

        if (!parsed || typeof parsed !== 'object') {
            return { before: null, after: null };
        }

        let before = parsed.before ?? parsed.beforeData ?? parsed.before_data ?? null;
        let after = parsed.after ?? parsed.afterData ?? parsed.after_data ?? null;

        before = parseJsonValue(before);
        after = parseJsonValue(after);

        const hasExplicitBeforeAfter = 'before' in parsed || 'after' in parsed || 'before_data' in parsed || 'after_data' in parsed || 'beforeData' in parsed || 'afterData' in parsed;
        if (before == null && after == null && !hasExplicitBeforeAfter && Object.keys(parsed).length > 0) {
            before = parsed;
            after = null;
        }

        return { before, after };
    } catch (error) {
        console.error('解析数据快照失败:', error);
        return { before: null, after: null };
    }
};

const hasSnapshotData = data => {
    if (data === null || data === undefined) return false;
    if (typeof data === 'object' && !Array.isArray(data)) {
        return Object.keys(data).length > 0;
    }
    return true;
};

const createDiffPatcher = () => {
    return create({
        objectHash: obj => {
            if (obj === null || typeof obj !== 'object') {
                return JSON.stringify(obj);
            }
            return obj.id || obj._id || obj.key || JSON.stringify(obj);
        },
        arrays: {
            detectMove: true,
            includeValueOnMove: false,
        },
        textDiff: {
            minLength: 60,
        },
    });
};

const formatJsonForDisplay = data => {
    if (data === null || data === undefined) {
        return '无数据';
    }
    return JSON.stringify(data, null, 2);
};

const buildVisualDiffHtml = (beforeLocalized, afterLocalized) => {
    if (!hasSnapshotData(beforeLocalized) && !hasSnapshotData(afterLocalized)) {
        return '';
    }

    const delta = createDiffPatcher().diff(beforeLocalized, afterLocalized);

    if (!delta) {
        return '<div class="diff-no-change">变更前后字段一致，无可视化差异；请查看下方原始数据对比。</div>';
    }

    try {
        return formatHtmlDiff(delta, beforeLocalized);
    } catch (error) {
        console.error('生成可视化差异失败:', error);
        return '<div class="diff-error">生成差异对比失败</div>';
    }
};

/**
 * 数据快照对比内容区（不含 Drawer，供操作记录抽屉内嵌）
 */
export default function SnapshotDiffPanel({ dataSnapshot, data, expanded = false }) {
    const snapshotSource = dataSnapshot ?? data;

    const { beforeLocalized, afterLocalized, hasData, visualDiffHtml } = useMemo(() => {
        if (!snapshotSource) {
            return {
                beforeLocalized: null,
                afterLocalized: null,
                hasData: false,
                visualDiffHtml: '',
            };
        }

        const { before, after } = parseDataSnapshot(snapshotSource);
        const beforeLocalizedValue = normalizeDatesToLocal(before);
        const afterLocalizedValue = normalizeDatesToLocal(after);
        const hasDataValue = hasSnapshotData(beforeLocalizedValue) || hasSnapshotData(afterLocalizedValue);

        return {
            beforeLocalized: beforeLocalizedValue,
            afterLocalized: afterLocalizedValue,
            hasData: hasDataValue,
            visualDiffHtml: hasDataValue ? buildVisualDiffHtml(beforeLocalizedValue, afterLocalizedValue) : '',
        };
    }, [snapshotSource]);

    if (!hasData) {
        return (
            <div className="no-data-container">
                <Text type="secondary">暂无数据快照信息</Text>
            </div>
        );
    }

    const panelClassName = expanded ? 'snapshot-diff-panel snapshot-diff-panel--expanded' : 'snapshot-diff-panel';

    return (
        <div className={panelClassName}>
            <div className="diff-content">
                <div className="visual-diff-section">
                    <Title level={4}>数据变化对比</Title>
                    <div className="diff-container" dangerouslySetInnerHTML={{ __html: visualDiffHtml }} />
                </div>

                <div className="raw-data-section">
                    <Title level={4}>原始数据对比</Title>
                    <Row gutter={16}>
                        <Col span={12}>
                            <div className="data-column">
                                <Title level={5} className="column-title before-title">
                                    变更前数据
                                </Title>
                                <pre className="json-data before-data">{formatJsonForDisplay(beforeLocalized)}</pre>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="data-column">
                                <Title level={5} className="column-title after-title">
                                    变更后数据
                                </Title>
                                <pre className="json-data after-data">{formatJsonForDisplay(afterLocalized)}</pre>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
}
